import { IHubFolder, ISidebarMoveItem } from 'types'
import {
  DropTarget,
  DropZone,
  SidebarNode,
  canNestFoldersIn,
  childrenOf,
  findNode,
  insertAt,
  folderAncestorIds,
  resolveDrop
} from 'components/sidebar/components/folders/tree'

/**
 * The pure half of the sidebar's drag and drop: which rows a drag carries, which part of a row the
 * pointer is over, and what dropping there would do. SidebarDnd wires these to dnd-kit.
 */

/**
 * The droppable id of the tree as a whole, behind its rows: a drop on its empty space, below the last
 * row, goes to the end of the top level. Not a public id, so it can't clash with a row's.
 */
export const TREE_DROP_ID = 'sidebar-tree'

// Not a public id, so an Empty row's droppable id can't clash with a row's.
const EMPTY_FOLDER_DROP_PREFIX = 'sidebar-empty:'

/** The droppable id of the Empty row inside an open folder with nothing in it: a drop there goes into the folder. */
export const emptyFolderDropId = (folderId: string): string => `${EMPTY_FOLDER_DROP_PREFIX}${folderId}`

/** The folder whose Empty row `overId` is, or null for any other droppable. */
const emptyFolderOf = (overId: string): string | null =>
  overId.startsWith(EMPTY_FOLDER_DROP_PREFIX) ? overId.slice(EMPTY_FOLDER_DROP_PREFIX.length) : null

/**
 * The row showing where a drag would drop, and how: a line on its top (`before`) or bottom (`after`)
 * edge, or a ring round it (`into`). A null row is the hub's top level as a whole.
 */
export interface RowDropIndicator {
  rowId: string | null
  kind: DropZone
}

/**
 * What a drop would do. A null indicator means it would leave everything where it already is: it's
 * allowed, but there's nothing to show and nothing to send.
 */
export interface DropPlan {
  target: DropTarget
  indicator: RowDropIndicator | null
}

/**
 * The part of a row the pointer is over, from its offset down the row (0 top, 1 bottom). A folder's
 * top and bottom quarters drop beside it and its middle half drops into it; a document or table
 * only has beside, split at halfway.
 */
export const zoneFor = (offset: number, isFolder: boolean): DropZone => {
  if (!isFolder) return offset < 0.5 ? 'before' : 'after'
  if (offset < 0.25) return 'before'
  return offset > 0.75 ? 'after' : 'into'
}

/**
 * What a drag starting on `activeId` carries, as a move request names it. A selected row brings the
 * whole selection; any other row goes alone. Items are in the order the sidebar shows them, and an
 * item inside a folder that is also being moved is left to travel with that folder.
 */
export const dragGroup = (
  tree: SidebarNode[],
  folders: IHubFolder[],
  activeId: string,
  selected: string[],
  visible: string[]
): ISidebarMoveItem[] => {
  const chosen = new Set(selected.includes(activeId) ? selected : [activeId])
  return visible.flatMap((id) => {
    if (!chosen.has(id)) return []
    const node = findNode(tree, id)
    if (!node) return []
    const carriedByFolder = folderAncestorIds(folders, node.parentId).some((ancestor) => chosen.has(ancestor))
    return carriedByFolder ? [] : [{ kind: node.kind, id }]
  })
}

/**
 * The target's children, in order, once `items` have moved there, placed the way applyMove places
 * them: at `index` over the children with the moving items taken out (null appends), folders among
 * folders and resources among resources.
 */
const arrangementAfter = (tree: SidebarNode[], items: ISidebarMoveItem[], target: DropTarget): string[] => {
  const moving = new Set(items.map((item) => item.id))
  const kept = childrenOf(tree, target.parentFolderId).filter((node) => !moving.has(node.id))
  const keptFolders = kept.filter((node) => node.kind === 'folder').map((node) => node.id)
  const keptResources = kept.filter((node) => node.kind !== 'folder').map((node) => node.id)
  const movingFolders = items.filter((item) => item.kind === 'folder').map((item) => item.id)
  const movingResources = items.filter((item) => item.kind !== 'folder').map((item) => item.id)
  const at = target.index ?? keptFolders.length + keptResources.length
  const folderAt = Math.min(at, keptFolders.length)
  const resourceAt = Math.min(Math.max(0, at - keptFolders.length), keptResources.length)
  return [...insertAt(keptFolders, folderAt, movingFolders), ...insertAt(keptResources, resourceAt, movingResources)]
}

/** Whether every one of `items` is in the folder (null = top level) already. */
const allIn = (tree: SidebarNode[], items: ISidebarMoveItem[], parentFolderId: string | null): boolean =>
  items.every((item) => findNode(tree, item.id)?.parentId === parentFolderId)

/** Whether moving `items` to `target` would leave them all where they are. */
const staysPut = (tree: SidebarNode[], items: ISidebarMoveItem[], target: DropTarget): boolean => {
  if (!allIn(tree, items, target.parentFolderId)) return false
  const now = childrenOf(tree, target.parentFolderId).map((node) => node.id)
  return arrangementAfter(tree, items, target).join('/') === now.join('/')
}

/** The plan for a drop at `target` shown by `indicator`: refused past the nesting rules, and no indicator if nothing moves. */
const planAt = (
  tree: SidebarNode[],
  folders: IHubFolder[],
  items: ISidebarMoveItem[],
  target: DropTarget,
  indicator: RowDropIndicator
): DropPlan | null => {
  const folderIds = items.filter((item) => item.kind === 'folder').map((item) => item.id)
  if (!canNestFoldersIn(folders, folderIds, target.parentFolderId)) return null
  return { target, indicator: staysPut(tree, items, target) ? null : indicator }
}

/** The end of the top level, ringing the whole tree. */
const TOP_LEVEL_END: DropTarget = { parentFolderId: null, index: null }
const TREE_RING: RowDropIndicator = { rowId: null, kind: 'into' }

/**
 * What dropping `items` on part `zone` of row `overId` would do, and where the indicator for it
 * goes; null when the drop isn't allowed (resolveDrop's rules), so nothing is shown. A drop that
 * would change nothing (just after the row above, just before the row below) plans no indicator.
 *
 * Every level shows its folders before its documents and tables, so some edges can't be dropped
 * on as they look, and the plan shows where things really land instead:
 * - Documents and tables can't go between folders, so for a drag of only those a folder row is all
 *   "into". The one exception is a top level with no documents or tables of its own, which has no
 *   row to drop beside: there a top-level folder's edges drop at the top level, ringing the tree.
 * - Folders can't go between documents and tables, so for a drag of only folders a document or
 *   table row drops after the level's last folder, shown as a line above its first document or table.
 * - The bottom edge of an open folder sits right above its first child, so a drop there goes to the
 *   top of the folder, not after the folder's whole subtree further down.
 *
 * A drop that appends (index null) rings the folder it goes into, or the whole tree for the top
 * level. One at an index draws a line on the row it was aimed at.
 *
 * `overId` may be TREE_DROP_ID, the tree's empty space: that appends to the top level. Or it may be
 * an empty folder's Empty row (emptyFolderDropId), which drops into that folder as its row's middle
 * does, under the same rules.
 *
 * For a sidebar the viewer has sorted, see planSortedDrop.
 */
export const planDrop = (
  tree: SidebarNode[],
  folders: IHubFolder[],
  items: ISidebarMoveItem[],
  overId: string,
  zone: DropZone,
  expanded: Set<string>
): DropPlan | null => {
  if (items.length === 0) return null
  if (overId === TREE_DROP_ID) return planAt(tree, folders, items, TOP_LEVEL_END, TREE_RING)
  const emptyFolder = emptyFolderOf(overId)
  if (emptyFolder !== null) return planDrop(tree, folders, items, emptyFolder, 'into', expanded)
  const over = findNode(tree, overId)
  if (!over) return null
  const draggedIds = new Set(items.map((item) => item.id))
  if (draggedIds.has(over.id)) return null
  const isFolder = over.kind === 'folder'
  const onlyResources = items.every((item) => item.kind !== 'folder')
  const onlyFolders = items.every((item) => item.kind === 'folder')
  const kept = childrenOf(tree, over.parentId).filter((node) => !draggedIds.has(node.id))
  const firstResource = kept.find((node) => node.kind !== 'folder')

  if (isFolder && onlyResources && zone !== 'into' && over.parentId === null && !firstResource) {
    return planAt(tree, folders, items, TOP_LEVEL_END, TREE_RING)
  }
  if (!isFolder && onlyFolders && firstResource) {
    const index = kept.filter((node) => node.kind === 'folder').length
    return planAt(
      tree,
      folders,
      items,
      { parentFolderId: over.parentId, index },
      { rowId: firstResource.id, kind: 'before' }
    )
  }

  const aim = isFolder && onlyResources ? 'into' : zone
  const topOfOpenFolder = isFolder && aim === 'after' && expanded.has(over.id)
  const resolved = resolveDrop(tree, folders, overId, topOfOpenFolder ? 'into' : aim, items)
  if (!resolved) return null
  const target = topOfOpenFolder ? { ...resolved, index: 0 } : resolved
  if (staysPut(tree, items, target)) return { target, indicator: null }
  if (topOfOpenFolder) return { target, indicator: { rowId: overId, kind: 'after' } }
  if (target.index === null) return { target, indicator: { rowId: target.parentFolderId, kind: 'into' } }
  return { target, indicator: { rowId: overId, kind: aim === 'after' ? 'after' : 'before' } }
}

/**
 * A drop that puts rows next to another row, rather than into a folder or at the end of a level. The
 * top of an open folder is drawn as a line under the folder's own row, but drops into that folder, so
 * it isn't a reorder either.
 */
export const isReorder = (plan: DropPlan | null): boolean => {
  const indicator = plan?.indicator
  if (!plan || !indicator || indicator.rowId === null || indicator.kind === 'into') return false
  return indicator.rowId !== plan.target.parentFolderId
}

/**
 * planDrop for a sidebar the viewer has sorted (Name or Date created), planned on its rows as they
 * show (`shown`), so the line goes where the pointer is. A drop between rows is a reorder, which
 * puts the sidebar back in the shared order (manualDropFor). A drop into a folder, or at the end of
 * a level, files the rows there and keeps the sort, so one into the folder they're already in moves
 * nothing: it would only shuffle the shared order out of sight.
 */
export const planSortedDrop = (
  shown: SidebarNode[],
  folders: IHubFolder[],
  items: ISidebarMoveItem[],
  overId: string,
  zone: DropZone,
  expanded: Set<string>
): DropPlan | null => {
  const plan = planDrop(shown, folders, items, overId, zone, expanded)
  if (!plan?.indicator || isReorder(plan)) return plan
  return allIn(shown, items, plan.target.parentFolderId) ? { ...plan, indicator: null } : plan
}

/**
 * Where a reorder planned on the sorted rows goes in the shared order (`manual`): beside the same row,
 * on the same side, under the same rules. Null for a drop that isn't a reorder.
 */
export const manualDropFor = (
  manual: SidebarNode[],
  folders: IHubFolder[],
  items: ISidebarMoveItem[],
  plan: DropPlan,
  expanded: Set<string>
): DropPlan | null => {
  if (!isReorder(plan) || !plan.indicator?.rowId) return null
  return planDrop(manual, folders, items, plan.indicator.rowId, plan.indicator.kind, expanded)
}

/**
 * The collapsed folder a drag is hovering into, which springs open after a moment; null otherwise.
 * Only a drop that is allowed springs a folder, so a folder can't be sprung open under itself.
 */
export const springFolder = (plan: DropPlan | null, overId: string | null, expanded: Set<string>): string | null => {
  if (!plan?.indicator || !overId || plan.target.index !== null) return null
  if (plan.target.parentFolderId !== overId || expanded.has(overId)) return null
  return overId
}

/** What a drop would do, for screen readers, naming rows with `nameOf`. */
export const describeDrop = (plan: DropPlan | null, nameOf: (id: string) => string): string => {
  if (!plan) return "Can't drop here."
  if (!plan.indicator) return 'Nothing will move.'
  const { rowId, kind } = plan.indicator
  if (rowId === null) return 'Into the top level.'
  const name = nameOf(rowId)
  if (kind === 'into') return `Into ${name}.`
  if (kind === 'before') return `Before ${name}.`
  return plan.target.parentFolderId === rowId ? `Into the top of ${name}.` : `After ${name}.`
}

/**
 * Which drops are bound to come to the same thing, as a key per row and zone, so a keyboard drag can
 * plan each group once. For a drag of only folders, a document or table row drops after its level's
 * last folder whichever row and zone it is (planDrop), so each level's documents and tables share
 * their folder as a key. Without that, ↓ past the last folder above 2,000 documents planned all 4,000
 * of their bands on every press. Any other row and zone is a key of its own.
 */
export const dropGroups = (
  tree: SidebarNode[],
  items: ISidebarMoveItem[]
): ((overId: string, zone: DropZone) => string) => {
  // The folder (null = top level) of each document and table, for a drag of only folders.
  const resourceParents = new Map<string, string | null>()
  const walk = (nodes: SidebarNode[]): void => {
    for (const node of nodes) {
      if (node.kind === 'folder') walk(node.children)
      else resourceParents.set(node.id, node.parentId)
    }
  }
  if (items.length > 0 && items.every((item) => item.kind === 'folder')) walk(tree)
  return (overId, zone) => {
    const parent = resourceParents.get(overId)
    return parent === undefined ? `${zone}:${overId}` : `level:${parent ?? ''}`
  }
}

/**
 * Where a drop would put things, as a key: two drops with the same key come to the same thing (after
 * one row and before the next, or after a folder's last row and into the folder). A drop that changes
 * nothing is `none`, the same as the drag's own rows, and a refused one is `refused`. The keyboard
 * steps from one outcome to the next.
 * The indicator is deliberately not part of the key: the keyboard merges bands that land in the same
 * place even when one would show a ring and the other a line.
 */
export const dropOutcome = (tree: SidebarNode[], items: ISidebarMoveItem[], plan: DropPlan | null): string => {
  if (!plan) return 'refused'
  if (!plan.indicator) return 'none'
  const parent = plan.target.parentFolderId ?? ''
  return `${parent}:${arrangementAfter(tree, items, plan.target).join('/')}`
}
